import { z } from 'zod';

// ─── Chart sub-schemas ────────────────────────────────────────────────────────

const pointSchema = z.object({ x: z.string(), y: z.number() });

const barDataSchema = z.object({
  index: z.string(),
  data: z.array(pointSchema),
});

const barChartSchema = z.object({
  title: z.string(),
  xAxisTitle: z.string(),
  yAxisTitle: z.string(),
  indexBy: z.string(),
  data: z.array(pointSchema),
  tooltipTemplate: z.string().optional(),
});

const multipleBarChartSchema = z.object({
  title: z.string(),
  xAxisTitle: z.string(),
  yAxisTitle: z.string(),
  indexBy: z.string(),
  data: z.array(barDataSchema),
  tooltipTemplate: z.string().optional(),
});

const pieDataPointSchema = z.object({
  id: z.string(),
  label: z.string(),
  value: z.number(),
});

const pieChartSchema = z.object({
  title: z.string(),
  xAxisTitle: z.string(),
  yAxisTitle: z.string(),
  data: z.array(pieDataPointSchema),
  tooltipTemplate: z.string().optional(),
});

const chartOptionsSchema = z.object({
  n: z.number().optional(),
  unit: z.string().optional(),
  isPrefix: z.boolean().optional(),
}).optional();

const multipleChartOptionsSchema = z.object({
  seriesToN: z.record(z.string(), z.number()).optional(),
  unit: z.string().optional(),
  isPrefix: z.boolean().optional(),
}).optional();

const dataTableSchema = z.object({
  records: z.array(z.array(z.string().nullable())),
  rowNames: z.array(z.array(z.string().nullable())),
  columnNames: z.array(z.array(z.string().nullable())),
  /**
   * Optional per-cell metadata (arrow indicators, significance flags).
   * Mirrors the `metadata` field of `QuantFileTableData`.
   */
  metadata: z.object({
    arrowColors: z.array(z.array(z.number())).optional(),
    arrowDirection: z.array(z.array(z.string())).optional(),
    isSignificant: z.array(z.array(z.boolean())).optional(),
  }).optional(),
});

const sortConfigSchema = z.object({
  columnIndex: z.number(),
  direction: z.enum(['asc', 'desc']),
}).nullable().optional();

// ─── ChartWidgetDataSchema ────────────────────────────────────────────────────

/**
 * Zod schema for ChartWidgetData — mirrors the QuickChartData discriminated union.
 * All 8 chart variants are validated individually so a bad shape is caught early.
 */
export const ChartWidgetDataSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('bar'), chart: barChartSchema, colors: z.array(z.string()).optional(), options: chartOptionsSchema, insights: z.string().optional() }),
  z.object({ type: z.literal('stacked-bar'), chart: barChartSchema, colors: z.array(z.string()).optional(), options: chartOptionsSchema, insights: z.string().optional() }),
  z.object({ type: z.literal('pie'), chart: pieChartSchema, colors: z.array(z.string()).optional(), options: chartOptionsSchema, insights: z.string().optional() }),
  z.object({ type: z.literal('multiple-clustered-bar'), chart: multipleBarChartSchema, colors: z.array(z.string()).optional(), options: multipleChartOptionsSchema, insights: z.string().optional() }),
  z.object({ type: z.literal('multiple-stacked-bar'), chart: multipleBarChartSchema, colors: z.array(z.string()).optional(), options: multipleChartOptionsSchema, insights: z.string().optional() }),
  z.object({
    type: z.literal('data-table'),
    title: z.string(),
    headerColor: z.string(),
    data: dataTableSchema,
    customColumnNames: z.array(z.array(z.string())).optional(),
    customRowNames: z.array(z.array(z.string())).optional(),
    sortConfig: sortConfigSchema,
    insights: z.string().optional(),
  }),
  z.object({
    type: z.literal('kpi-widget'),
    title: z.string(),
    backgroundColor: z.string(),
    values: z.array(z.string()),
    labels: z.array(z.string()),
    insights: z.string().optional(),
  }),
  z.object({
    type: z.literal('progress-bar-widget'),
    title: z.string(),
    backgroundColor: z.string(),
    values: z.array(z.string()),
    labels: z.array(z.string()),
    colors: z.array(z.string()).optional(),
    minValues: z.array(z.string()).optional(),
    maxValues: z.array(z.string()).optional(),
    units: z.array(z.string()).optional(),
    insights: z.string().optional(),
  }),
  z.object({
    type: z.literal('semi-circle-progress-widget'),
    title: z.string(),
    backgroundColor: z.string(),
    values: z.array(z.string()),
    labels: z.array(z.string()),
    colors: z.array(z.string()).optional(),
    minValues: z.array(z.string()).optional(),
    maxValues: z.array(z.string()).optional(),
    units: z.array(z.string()).optional(),
    insights: z.string().optional(),
  }),
]);

// ─── Canonical per-widget data shapes ────────────────────────────────────────

/**
 * `ChartWidgetData` is derived from `ChartWidgetDataSchema`, making the Zod
 * schema the single source of truth for both TypeScript types and runtime
 * validation. `QuickChartData` in widget-grid.ts re-exports this type so all
 * existing consumers continue to work without modification.
 */
export type ChartWidgetData = z.infer<typeof ChartWidgetDataSchema>;

/**
 * Text widget data — a `content` string.
 * For markdown mode this is the raw markdown; for rich text a serialised representation.
 */
export const TextWidgetDataSchema = z.object({
  content: z.string(),
});
export interface TextWidgetData {
  content: string;
}

/** Metric card widget data — parallel arrays of display values and labels */
export const MetricCardWidgetDataSchema = z.object({
  values: z.array(z.string()),
  labels: z.array(z.string()),
});
export interface MetricCardWidgetData {
  values: string[];
  labels: string[];
}

/** Code widget data — arbitrary key/value pairs passed into the iframe sandbox */
export const CodeWidgetDataSchema = z.record(z.string(), z.unknown());
export type CodeWidgetData = Record<string, unknown>;

// ─── CanonicalWidgetDataSchema ────────────────────────────────────────────────

/**
 * Runtime-validated discriminated union for all widget data shapes.
 *
 * Agents must return JSON matching one of these variants. The proxy endpoint
 * rejects responses that don't conform so that malformed data never reaches
 * the rendering layer.
 *
 * Agents should return extra fields where useful — they are stripped by
 * `safeParse`. But required fields per variant (e.g. `chart`, `content`,
 * `values`/`labels`) must be present or the response is rejected.
 */
export const CanonicalWidgetDataSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('chart'), data: ChartWidgetDataSchema }),
  z.object({ type: z.literal('text'), data: TextWidgetDataSchema }),
  z.object({ type: z.literal('metric_card'), data: MetricCardWidgetDataSchema }),
  z.object({ type: z.literal('code'), data: CodeWidgetDataSchema }),
]);

/**
 * CanonicalWidgetData is the data shape that the DashboardDataContext holds
 * for each widget. Widgets read this via `useDashboardData(widgetId)` and
 * transform it into the specific props expected by their Widget UI.
 */
export type CanonicalWidgetData =
  | { type: 'chart'; data: ChartWidgetData }
  | { type: 'text'; data: TextWidgetData }
  | { type: 'metric_card'; data: MetricCardWidgetData }
  | { type: 'code'; data: CodeWidgetData };
